import React, { useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import shortid from 'shortid';

import { base } from '@pagerland/common/src/utils';

const scaleRotate = (scale = 0.8, rotate = `10deg`) => keyframes`
  from {
    transform: scale(${scale}) rotate(${rotate});
  }

  to {
    transform: scale(1) rotate(0);
  }
`;

const opacity = (from = 0, to = 1) => keyframes`
  from {
    opacity: ${from};
  }

  to {
    opacity: ${to};
  }
`;

const AnimatedSvg = styled.svg`
  ${base};

  transform: translate3d(0, 0, 0);
  animation: none !important;

  .liquid & {
    .layer-1 {
      opacity: 0;
      transform-origin: 250px 380px;
      animation: 600ms ${scaleRotate(0.8, '3deg')} forwards, 600ms ${opacity()} forwards;
    }
    .layer-2 {
      opacity: 0;
      transform-origin: 250px 380px;
      animation: 600ms ${scaleRotate(0.8, '-3deg')} forwards, 600ms ${opacity(0, 0.5)} forwards;
    }
    .layer-3 {
      opacity: 0;
      transform-origin: 250px 270px;
      animation: 600ms ${scaleRotate(0.8, '10deg')} forwards 50ms, 600ms ${opacity()} forwards 50ms;
    }
    .layer-4 {
      opacity: 0;
      transform-origin: 210px 70px;
      animation: 600ms ${scaleRotate(0.8, '-15deg')} forwards 60ms, 600ms ${opacity()} forwards 60ms;
    }
    .layer-5 {
      opacity: 0;
      transform-origin: 290px 360px;
      animation: 600ms ${scaleRotate(0.8, '180deg')} forwards 60ms, 600ms ${opacity()} forwards 60ms;
    }
    .layer-6 {
      opacity: 0;
      transform-origin: 380px 125px;
      animation: 600ms ${scaleRotate(0.8, '-180deg')} forwards 70ms,
        600ms ${opacity()} forwards 70ms;
    }
    .layer-7 {
      opacity: 0;
      transform-origin: 55px 245px;
      animation: 600ms ${scaleRotate(0.8, '180deg')} forwards 60ms, 600ms ${opacity()} forwards 60ms;
    }
    .layer-8 {
      opacity: 0;
      transform-origin: 410px 415px;
      animation: 600ms ${scaleRotate(0.8, '180deg')} forwards 80ms, 600ms ${opacity()} forwards 80ms;
    }
    .layer-9 {
      opacity: 0;
      transform-origin: 600px 220px;
      animation: 600ms ${scaleRotate(0.8, '-180deg')} forwards 60ms,
        600ms ${opacity()} forwards 60ms;
    }
    .layer-10 {
      opacity: 0;
      transform-origin: 180px 230px;
      animation: 600ms ${scaleRotate(0.8, '180deg')} forwards 70ms, 600ms ${opacity()} forwards 70ms;
    }
    .layer-11 {
      opacity: 0;
      transform-origin: 300px 70px;
      animation: 600ms ${scaleRotate(0.8, '0')} forwards 80ms, 600ms ${opacity()} forwards 80ms;
    }
    .layer-12 {
      opacity: 0;
      transform-origin: 230px 360px;
      animation: 600ms ${scaleRotate(0.8, '0')} forwards 80ms, 600ms ${opacity()} forwards 80ms;
    }
    .layer-13 {
      opacity: 0;
      transform-origin: 610px 330px;
      animation: 600ms ${scaleRotate(0.8, '0')} forwards 80ms, 600ms ${opacity()} forwards 80ms;
    }
    .layer-14 {
      opacity: 0;
      transform-origin: 170px 90px;
      animation: 600ms ${scaleRotate(0.8, '0')} forwards 90ms, 600ms ${opacity()} forwards 90ms;
    }
  }
`;

const B = ({ ...props }) => {
  const shortId = useRef(shortid.generate());

  return (
    <AnimatedSvg viewBox="0 0 614 500" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <g clipPath="url(#clip0)">
        <path
          className="layer-1"
          d="M164.792 373.523C135.295 369.027 117.208 331.968 115.001 323.119C108.364 296.51 124.501 265.766 144.306 246.263C156.963 233.801 173.357 225.78 186.669 214.018C210.586 192.891 222.937 159.513 218.379 128.333C215.515 108.736 206.517 90.4048 204.629 70.6771C202.741 50.9462 211.224 27.4586 230.48 21.9765C238.527 19.688 247.108 20.9511 255.133 23.0768C274.554 28.2138 292.54 38.3739 306.864 52.2972C316.279 61.4546 325.107 72.7703 338.064 75.43C354.009 78.7017 369.592 67.2915 379.642 54.3318C389.688 41.3721 396.657 25.6323 409.568 15.482C451.761 -17.6904 517.572 10.5729 531.792 25.1668C577.139 71.7188 557.786 135.476 547.447 160.431C537.108 185.384 524.073 209.806 520.356 236.455C510.824 304.707 563.991 368.405 557.558 436.973C556.767 445.402 555.016 453.905 550.891 461.356C546.067 470.068 538.273 476.839 529.891 482.275C507.862 496.573 480.637 502.622 454.835 498.949C397.152 490.743 357.537 439.373 307.274 410.556C262.464 384.868 215.72 381.287 164.792 373.523V373.523Z"
          fill={`url(#${shortId.current}_paint0_linear)`}
        />
        <path
          className="layer-2"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M557.818 126.887C557.819 126.883 557.82 126.88 557.821 126.876C555.923 126.648 553.969 126.492 551.954 126.414C549.646 126.326 547.426 126.284 545.294 126.284C532.556 126.284 522.835 127.755 514.68 129.223C513.324 129.468 512.011 129.713 510.735 129.951C504.337 131.143 498.86 132.163 493.465 132.163C488.692 132.163 483.982 131.366 478.757 129.175C451.135 117.605 450.009 98.6261 450.23 72.8825C450.448 48.1383 439.793 25.344 415.583 14.6533C414.587 14.2171 413.578 13.8004 412.552 13.4033C411.608 14.0706 410.677 14.7673 409.756 15.49C401.206 22.2117 395.261 31.3847 389.304 40.5782C386.265 45.2676 383.222 49.9623 379.829 54.3398C370.961 65.7759 357.787 76.0044 343.854 76.0044C343.628 76.0044 343.401 76.0017 343.175 75.9962C343.365 76.0001 343.555 76.002 343.745 76.002C357.678 76.002 370.853 65.7735 379.72 54.3374C383.114 49.9598 386.156 45.2651 389.195 40.5758C395.153 31.3822 401.097 22.2093 409.647 15.4875C410.568 14.7648 411.499 14.0682 412.443 13.4008C403.872 10.1064 394.229 8.35498 384.636 8.35498C367.161 8.35498 349.852 14.1658 339.506 27.0442C330.951 37.6933 327.801 52.6302 324.707 67.3031C324.598 67.8224 324.488 68.3413 324.379 68.8596C324.41 68.8832 324.441 68.9067 324.473 68.9302C322.742 77.1071 320.967 85.149 318.214 92.2618C302.325 133.338 266.528 166.175 224.234 178.471C219.716 179.786 215.129 180.877 210.523 181.889C204.95 194.054 196.928 205.126 186.856 214.026C180.795 219.382 174.095 223.962 167.399 228.539C159.39 234.013 151.388 239.484 144.494 246.271C138.735 251.942 133.285 258.567 128.614 265.761C132.556 289.382 146.818 321.649 180.7 332.115C188.298 334.463 196.001 335.387 203.709 335.387C217.893 335.387 232.103 332.265 245.762 329.14C246.345 329.007 246.927 328.874 247.507 328.741C260.535 325.757 273.029 322.896 284.475 322.896C293.737 322.896 302.312 324.771 309.933 329.97C325.78 340.784 333.504 358.432 341.818 377.426C355.274 408.167 370.274 442.436 423.747 456.986C429.708 458.607 435.643 459.408 441.457 459.408C457.187 459.408 472.061 453.541 484.288 442.154C503.692 424.083 506.726 394.175 509.661 365.234C510.888 353.138 512.098 341.21 514.478 330.387C516.601 320.738 521.276 312.075 527.5 303.96C527.49 303.925 527.48 303.889 527.47 303.854C536.578 292.013 548.98 281.351 561.549 270.546C589.399 246.604 618.067 221.959 613.522 182.222C612.029 169.166 598.074 131.791 557.818 126.887ZM144.385 246.269C138.626 251.939 133.177 258.564 128.505 265.759C127.509 259.795 127.174 254.381 127.346 250.009C128.095 230.972 151.303 200.195 168.882 192.841C179.607 188.352 191.007 185.97 202.423 183.585C205.088 183.028 207.755 182.471 210.414 181.887C204.841 194.052 196.82 205.124 186.747 214.024C180.686 219.38 173.986 223.96 167.29 228.536C159.281 234.011 151.279 239.481 144.385 246.269Z"
          fill={`url(#${shortId.current}_paint1_linear)`}
        />
        <path
          className="layer-3"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M601.106 357.85C591.861 369.41 578.055 376.513 563.897 380.82C501.98 399.672 431.204 370.734 371.92 396.706C344.656 408.65 320.182 425.565 293.843 439.433C206.911 485.204 135.921 379.7 162.537 324.567C187.346 273.18 208.812 290.349 209.763 239.025C210.486 199.791 180.608 195.37 167.938 158.229C151.54 110.147 174.767 45.9312 218.575 20.2006C262.383 -5.5235 325.57 7.27669 353.615 49.6358C362.431 62.9504 368.261 78.8627 380.853 88.6842C403.074 106.006 434.759 97.1254 462.73 93.7594C482.369 91.3927 502.387 92.2359 521.76 96.2335C542.337 100.482 564.154 110.127 572.391 129.458C579.448 146.025 574.562 165.183 567.569 181.772C560.574 198.362 551.406 214.577 549.831 232.514C546.712 268.014 572.632 268.584 593.18 285.492C613.529 302.231 617.536 337.305 601.106 357.85V357.85Z"
          fill={`url(#${shortId.current}_paint2_linear)`}
        />
        <path
          className="layer-4"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M37.2709 261.648C33.231 259.734 29.318 257.166 26.9839 253.357C21.7004 244.737 26.1212 233.144 22.5696 223.678C18.5329 212.915 5.31601 207.609 1.60813 196.726C-2.1193 185.801 5.42345 174.111 14.4767 166.946C27.9117 156.314 45.5559 151.157 62.6044 152.886C79.6496 154.615 95.9005 163.209 106.93 176.322C143.055 219.283 85.7958 284.664 37.2709 261.648V261.648Z"
          fill={`url(#${shortId.current}_paint3_linear)`}
        />
        <path
          className="layer-5"
          d="M300.585 352.876C299.859 351.711 298.332 351.356 297.163 352.079L281.703 361.682C280.541 362.408 280.18 363.935 280.906 365.1C281.629 366.266 283.159 366.62 284.324 365.898L299.784 356.294C300.949 355.572 301.307 354.042 300.585 352.876V352.876Z"
          fill="white"
        />
        <path
          className="layer-5"
          d="M284.859 349.149C283.693 349.872 283.335 351.405 284.058 352.567L293.665 368.03C294.387 369.196 295.917 369.551 297.079 368.828C298.248 368.105 298.603 366.575 297.88 365.41L288.274 349.947C287.554 348.785 286.021 348.426 284.859 349.149Z"
          fill="white"
        />
        <path
          className="layer-6"
          d="M387.406 120.666C386.683 119.5 385.153 119.142 383.988 119.868L368.528 129.472C367.363 130.194 367.005 131.728 367.727 132.89C368.453 134.055 369.98 134.413 371.149 133.691L386.608 124.084C387.771 123.361 388.132 121.831 387.406 120.666V120.666Z"
          fill="white"
        />
        <path
          className="layer-6"
          d="M371.382 116.938C370.213 117.661 369.858 119.194 370.581 120.359L380.188 135.819C380.907 136.981 382.44 137.34 383.602 136.617C384.768 135.894 385.126 134.364 384.403 133.199L374.797 117.739C374.074 116.573 372.544 116.215 371.382 116.938Z"
          fill="white"
        />
        <path
          className="layer-7"
          d="M55.9524 223.226C54.5884 223.35 53.5825 224.557 53.7062 225.925L55.3632 244.051C55.4869 245.415 56.6946 246.42 58.0619 246.297C59.4259 246.17 60.4318 244.962 60.3081 243.598L58.6511 225.472C58.5274 224.108 57.3196 223.102 55.9524 223.226Z"
          fill="white"
        />
        <path
          className="layer-7"
          d="M45.6515 235.826C45.7752 237.19 46.9829 238.196 48.347 238.072L66.4762 236.415C67.8402 236.292 68.8461 235.084 68.7192 233.716C68.5955 232.352 67.3877 231.347 66.0237 231.47L47.8977 233.127C46.5337 233.254 45.5245 234.462 45.6515 235.826V235.826Z"
          fill="white"
        />
        <path
          className="layer-8"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M402.124 411.713C401.404 410.548 401.759 409.018 402.925 408.295C404.09 407.572 405.62 407.927 406.343 409.093L409.857 414.749L415.534 411.222C416.699 410.499 418.226 410.857 418.952 412.023C419.675 413.185 419.317 414.715 418.154 415.441L412.478 418.966L415.949 424.553C416.669 425.718 416.314 427.251 415.148 427.974C413.983 428.697 412.453 428.339 411.73 427.173L408.259 421.587L402.691 425.044C401.526 425.767 399.999 425.412 399.273 424.247C398.55 423.081 398.908 421.551 400.071 420.829L405.638 417.37L402.124 411.713Z"
          fill={`url(#${shortId.current}_paint4_linear)`}
        />
        <path
          className="layer-9"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M594.698 215.756C593.972 214.591 594.331 213.061 595.496 212.338C596.658 211.615 598.191 211.974 598.911 213.136L602.427 218.794L608.105 215.268C609.27 214.542 610.797 214.9 611.523 216.066C612.246 217.231 611.888 218.761 610.726 219.484L605.048 223.01L608.521 228.599C609.24 229.761 608.885 231.294 607.72 232.017C606.555 232.74 605.024 232.382 604.302 231.219L600.83 225.629L595.263 229.087C594.1 229.813 592.567 229.455 591.844 228.29C591.118 227.124 591.48 225.594 592.642 224.871L598.211 221.413L594.698 215.756Z"
          fill={`url(#${shortId.current}_paint5_linear)`}
        />
        <path
          className="layer-10"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M170.795 222.446C170.075 221.281 170.43 219.751 171.599 219.028C172.761 218.305 174.291 218.664 175.014 219.829L178.444 225.348L183.905 221.956C185.071 221.234 186.601 221.588 187.323 222.754C188.049 223.919 187.688 225.449 186.526 226.172L181.064 229.565L184.62 235.289C185.343 236.454 184.985 237.984 183.82 238.707C182.657 239.43 181.124 239.072 180.405 237.909L176.847 232.185L171.063 235.779C169.897 236.501 168.37 236.143 167.644 234.978C166.922 233.816 167.28 232.282 168.442 231.56L174.226 227.968L170.795 222.446Z"
          fill={`url(#${shortId.current}_paint6_linear)`}
        />
        <path
          className="layer-11"
          d="M283.231 68.1836H284.859C284.859 72.5328 286.617 76.4555 289.465 79.3072C292.32 82.1589 296.243 83.9169 300.596 83.9169C304.945 83.9169 308.867 82.1589 311.719 79.3072C314.568 76.4555 316.326 72.5328 316.329 68.1836C316.326 63.8344 314.568 59.9084 311.719 57.0567C308.867 54.2049 304.945 52.447 300.596 52.447C296.243 52.447 292.32 54.2049 289.465 57.0567C286.617 59.9084 284.859 63.8344 284.859 68.1836H281.604C281.604 57.6947 290.103 49.1949 300.596 49.1917C311.081 49.1949 319.581 57.6947 319.584 68.1836C319.581 78.6692 311.081 87.1722 300.596 87.1722C290.103 87.1722 281.604 78.6692 281.604 68.1836H283.231V68.1836Z"
          fill="white"
        />
        <path
          className="layer-12"
          d="M200.18 361.575H201.808C201.811 368.805 204.734 375.339 209.474 380.079C214.214 384.815 220.744 387.738 227.975 387.742C235.205 387.738 241.735 384.815 246.475 380.079C251.215 375.339 254.138 368.805 254.141 361.575C254.138 354.345 251.215 347.814 246.475 343.075C241.735 338.335 235.205 335.411 227.975 335.411C220.744 335.411 214.214 338.335 209.474 343.075C204.734 347.814 201.811 354.345 201.808 361.575H198.552C198.556 345.324 211.724 332.156 227.975 332.156C244.225 332.156 257.393 345.324 257.397 361.575C257.393 377.826 244.225 390.994 227.975 390.997C211.724 390.994 198.556 377.826 198.552 361.575H200.18Z"
          fill="white"
        />
        <path
          className="layer-13"
          d="M551.213 330.775H552.841C552.841 335.124 554.599 339.047 557.447 341.902C560.299 344.75 564.222 346.508 568.574 346.508C572.923 346.508 576.846 344.75 579.698 341.902C582.546 339.047 584.304 335.124 584.307 330.775C584.304 326.426 582.546 322.5 579.698 319.648C576.846 316.799 572.923 315.042 568.574 315.042C564.222 315.042 560.299 316.799 557.447 319.648C554.599 322.5 552.841 326.426 552.841 330.775H549.585C549.585 320.286 558.085 311.786 568.574 311.786C579.06 311.786 587.559 320.286 587.563 330.775C587.559 341.264 579.06 349.763 568.574 349.763C558.085 349.763 549.585 341.264 549.585 330.775H551.213Z"
          fill="white"
        />
        <path
          className="layer-14"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M93.4202 87.7221C93.4202 132.171 129.454 168.208 173.91 168.208C218.359 168.208 254.393 132.171 254.393 87.7221C254.393 43.2698 218.359 7.23267 173.91 7.23267C129.454 7.23267 93.4202 43.2698 93.4202 87.7221Z"
          fill={`url(#${shortId.current}_paint7_linear)`}
        />
      </g>
      <defs>
        <linearGradient
          id={`${shortId.current}_paint0_linear`}
          x1="560.525"
          y1="250"
          x2="263.067"
          y2="460.937"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FEBB75" />
          <stop offset={1} stopColor="#FE8E75" />
        </linearGradient>
        <linearGradient
          id={`${shortId.current}_paint1_linear`}
          x1="614"
          y1="233.881"
          x2="333.066"
          y2="474.31"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FEBB75" />
          <stop offset={1} stopColor="#FE8E75" />
        </linearGradient>
        <linearGradient
          id={`${shortId.current}_paint2_linear`}
          x1="611.296"
          y1="228.807"
          x2="337.527"
          y2="451.202"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FB948F" />
          <stop offset={1} stopColor="#DC2789" />
        </linearGradient>
        <linearGradient
          id={`${shortId.current}_paint3_linear`}
          x1="118.3"
          y1="209.49"
          x2="47.9159"
          y2="267.179"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#C36B84" />
          <stop offset={1} stopColor="#8735AE" />
        </linearGradient>
        <linearGradient
          id={`${shortId.current}_paint4_linear`}
          x1="419.325"
          y1="418.134"
          x2="406.787"
          y2="428.079"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#C36B84" />
          <stop offset={1} stopColor="#8735AE" />
        </linearGradient>
        <linearGradient
          id={`${shortId.current}_paint5_linear`}
          x1="611.897"
          y1="222.178"
          x2="599.358"
          y2="232.123"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#C36B84" />
          <stop offset={1} stopColor="#8735AE" />
        </linearGradient>
        <linearGradient
          id={`${shortId.current}_paint6_linear`}
          x1="187.698"
          y1="228.868"
          x2="175.16"
          y2="238.813"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#C36B84" />
          <stop offset={1} stopColor="#8735AE" />
        </linearGradient>
        <linearGradient
          id={`${shortId.current}_paint7_linear`}
          x1="254.393"
          y1="87.7205"
          x2="155.579"
          y2="166.092"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#C36B84" />
          <stop offset={1} stopColor="#8735AE" />
        </linearGradient>
        <clipPath id="clip0">
          <rect width="614" height="500" transform="matrix(-1 0 0 1 614 0)" fill="white" />
        </clipPath>
      </defs>
    </AnimatedSvg>
  );
};

export default B;
